import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Spinner from 'components/Spinner';
import PaginatorV3 from 'components/Paginator/PaginatorV3';
import { getPortfolioNotifications } from 'data/reducers/portfoliosManagementReducer';

const RecentAlertsSection = ({ portfolioId }) => {
	const dispatch = useDispatch();

	// Get notifications data from Redux store
	const {
		notifications,
		isLoadingNotifications,
		notificationsError,
		notificationsTotalCount,
		notificationsCurrentPage,
		notificationsPerPage,
	} = useSelector((state) => state.portfoliosManagement);

	// Fetch notifications for the portfolio with pagination
	const fetchNotifications = (page = 1, perPage = 10) => {
		if (!portfolioId) return;
		dispatch(getPortfolioNotifications(portfolioId, page, perPage));
	};

	// Handle notification pagination page change
	const handleNotificationPageChange = (page) => {
		fetchNotifications(page, notificationsPerPage || 10);
	};

	// Fetch notifications on component mount
	useEffect(() => {
		if (portfolioId) {
			fetchNotifications();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [portfolioId]);

	return (
		<div className="portfolio-detail-card mb-4">
			<div className="portfolio-detail-card-header">
				<h2 className="portfolio-detail-card-title">Recent Alerts</h2>
			</div>

			<div className="portfolio-detail-table-wrapper table-responsive">
				{isLoadingNotifications ? (
					<div className="d-flex justify-content-center align-items-center py-5">
						<Spinner size="sm" className="me-2" />
						Loading notifications...
					</div>
				) : notificationsError ? (
					<div className="portfolio-error-banner m-3">
						Error loading notifications: {notificationsError.message || 'Unknown error'}
					</div>
				) : (
					<>
						<table className="table portfolio-detail-table mb-0">
							<thead>
								<tr>
									<th scope="col" className="date-col">Date/Time</th>
									<th scope="col" className="property-col">Property</th>
									<th scope="col" className="alert-col">Alert</th>
								</tr>
							</thead>
							<tbody>
								{!notifications || !notifications.length ? (
									<tr>
										<td colSpan={3} className="text-center py-4 text-muted">
											No recent alerts found for this portfolio
										</td>
									</tr>
								) : (
									notifications.map((notification) => (
										<tr
											key={notification.id}
											className={`portfolio-table-row ${notification.read ? 'read' : 'unread'}`}
											style={{
												cursor: notification.url ? 'pointer' : 'default',
											}}
											onClick={() => {
												if (notification.url) {
													window.open(notification.url, '_blank');
												}
											}}
										>
											<td className="date-cell">
												{notification.datetime || notification.created_at || notification.date
													? new Date(
															notification.datetime ||
																notification.created_at ||
																notification.date,
														).toLocaleDateString()
													: '-'}
											</td>
											<td className="property-cell">
												{notification.property?.address ||
												notification.property_name?.address ? (
													<Link
														to={`/property/${
															notification.property?.address ||
															notification.property_name?.address
														}/overview`}
														className="property-link"
														onClick={(e) => e.stopPropagation()}
													>
														{notification.property?.address ||
															notification.property_name?.address}
													</Link>
												) : (
													'-'
												)}
											</td>
											<td className="alert-cell">
												<span
													className={`badge-status badge-alert-${(
														notification.notificationType?.name ||
														notification.notification_type?.name ||
														'default'
													)
														.toLowerCase()
														.replace(/\s+/g, '-')}`}
												>
													{notification.notificationType?.name ||
														notification.notification_type?.name ||
														'Alert'}
												</span>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>

						{/* Notifications Pagination */}
						{notifications && notifications.length > 0 && notificationsTotalCount && Math.ceil(notificationsTotalCount / (notificationsPerPage || 10)) > 1 && (
							<div className="portfolio-pagination-footer">
								<div className="portfolio-pagination-info">
									Showing {Math.min(((notificationsCurrentPage || 1) - 1) * (notificationsPerPage || 10) + 1, notificationsTotalCount)} to {Math.min((notificationsCurrentPage || 1) * (notificationsPerPage || 10), notificationsTotalCount)} of {notificationsTotalCount} alerts
								</div>
								<PaginatorV3
									currentPage={notificationsCurrentPage || 1}
									totalPages={Math.ceil(
										notificationsTotalCount / (notificationsPerPage || 10),
									)}
									onPageChange={handleNotificationPageChange}
								/>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default RecentAlertsSection;
